import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CheckCircle2, XCircle, Lock, AlertTriangle, FileText, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABEL = {
  manager_approved: { label: 'Manager Approved', color: 'bg-blue-500/10 text-blue-600 border-blue-500/30' },
  hr_approved: { label: 'HR Locked', color: 'bg-green-500/10 text-green-700 border-green-500/30' },
  hr_rejected: { label: 'HR Rejected', color: 'bg-red-500/10 text-red-600 border-red-500/30' },
};

function fmtMonth(d) { return d.toISOString().slice(0, 7); }

export default function TimesheetPage() {
  const [month, setMonth] = useState(fmtMonth(new Date()));
  const [filterStatus, setFilterStatus] = useState('manager_approved');
  const [data, setData] = useState({ items: [], by_employee: [] });
  const [loading, setLoading] = useState(false);
  const [actionTs, setActionTs] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (month) params.set('month', month);
      if (filterStatus) params.set('status', filterStatus);
      const res = await api.get(`/sshr/timesheet/v2/hr?${params.toString()}`);
      setData(res.data || { items: [], by_employee: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load HR timesheet view');
      setData({ items: [], by_employee: [] });
    } finally {
      setLoading(false);
    }
  }, [month, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAction = (ts, type) => {
    setActionTs(ts);
    setActionType(type);
    setComment('');
  };

  const submitAction = async () => {
    if (actionType === 'reject' && !comment.trim()) {
      toast.error('Rejection requires a comment');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/sshr/timesheet/v2/${actionTs.id}/hr-action`, {
        action: actionType,
        comment: comment.trim(),
      });
      toast.success(`Timesheet ${actionType}d`);
      setActionTs(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const monthDate = new Date(month + '-01');
  const prevMonth = () => {
    const d = new Date(monthDate); d.setMonth(d.getMonth() - 1); setMonth(fmtMonth(d));
  };
  const nextMonth = () => {
    const d = new Date(monthDate); d.setMonth(d.getMonth() + 1); setMonth(fmtMonth(d));
  };
  const monthLabel = monthDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const totals = useMemo(() => {
    const items = data.items || [];
    return {
      total: items.length,
      pending_hr: items.filter(i => i.status === 'manager_approved').length,
      hr_approved: items.filter(i => i.status === 'hr_approved').length,
      hr_rejected: items.filter(i => i.status === 'hr_rejected').length,
      late: items.filter(i => i.is_late).length,
      total_hours: items.reduce((s, i) => s + (i.total_hours || 0), 0),
    };
  }, [data]);

  const renderRow = (ts) => {
    const s = STATUS_LABEL[ts.status] || { label: ts.status, color: 'bg-muted' };
    return (
      <div key={ts.id} className="border rounded-lg p-3 space-y-2 hover:bg-muted/20" data-testid={`hr-ts-row-${ts.id}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="font-medium">{ts.employee_name}</p>
            <p className="text-xs text-muted-foreground">
              {ts.date} · {ts.total_hours}h · {ts.department || '—'}
              {ts.is_late && <span className="text-amber-600 ml-1">· LATE</span>}
              {ts.has_other_tasks && <span className="text-purple-600 ml-1">· has &quot;Other&quot;</span>}
            </p>
            <p className="text-[11px] text-muted-foreground">
              Manager: <span className="text-blue-600">{ts.manager_name}</span>
              {ts.manager_actioned_at && ` · ${new Date(ts.manager_actioned_at).toLocaleString()}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[11px] px-2 py-0.5 rounded-full border ${s.color}`}>{s.label}</span>
            {ts.locked && <Lock className="h-3.5 w-3.5 text-green-700" />}
          </div>
        </div>
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">View {(ts.rows || []).length} task entries</summary>
          <div className="mt-2 space-y-1.5 pl-2 border-l-2 border-muted">
            {(ts.rows || []).map((r, i) => (
              <div key={i}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{r.task_name}</span>
                  <Badge variant="outline">{r.hours}h</Badge>
                </div>
                <p className="text-muted-foreground whitespace-pre-wrap">{r.description}</p>
              </div>
            ))}
            {ts.is_late && ts.late_reason && (
              <p className="text-amber-700 mt-2"><strong>Late reason:</strong> {ts.late_reason}</p>
            )}
            {ts.manager_comment && (
              <p className="text-blue-700 mt-2"><strong>Manager note:</strong> {ts.manager_comment}</p>
            )}
            {ts.hr_comment && (
              <p className="text-green-700 mt-2"><strong>HR note:</strong> {ts.hr_comment}</p>
            )}
          </div>
        </details>
        {ts.status === 'manager_approved' && (
          <div className="flex gap-2 justify-end pt-1">
            <Button
              size="sm"
              variant="outline"
              className="text-red-600 border-red-500/30 hover:bg-red-500/10"
              onClick={() => openAction(ts, 'reject')}
              data-testid={`hr-reject-${ts.id}`}
            >
              <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
            </Button>
            <Button
              size="sm"
              onClick={() => openAction(ts, 'approve')}
              data-testid={`hr-approve-${ts.id}`}
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve & Lock
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="hr-timesheet-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">HR Timesheet Review</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Final approval for manager-approved timesheets. Locks the row for payroll once approved.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="hr-month-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium w-32 text-center">{monthLabel}</span>
          <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="hr-month-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Total Submissions</p>
          <p className="text-2xl font-bold">{totals.total}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Pending HR</p>
          <p className="text-2xl font-bold text-amber-600">{totals.pending_hr}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">HR Approved</p>
          <p className="text-2xl font-bold text-green-700">{totals.hr_approved}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Late</p>
          <p className="text-2xl font-bold text-red-600">{totals.late}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Total Hours</p>
          <p className="text-2xl font-bold">{totals.total_hours.toFixed(1)}h</p>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="queue">
        <TabsList>
          <TabsTrigger value="queue" data-testid="hr-tab-queue"><FileText className="h-4 w-4 mr-1" />Approval Queue</TabsTrigger>
          <TabsTrigger value="employees" data-testid="hr-tab-employees"><Users className="h-4 w-4 mr-1" />By Employee</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base">Submissions</CardTitle>
                <div className="flex gap-1">
                  {['manager_approved', 'hr_approved', 'hr_rejected', ''].map(st => (
                    <Button
                      key={st || 'all'}
                      variant={filterStatus === st ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFilterStatus(st)}
                      data-testid={`hr-filter-${st || 'all'}`}
                    >
                      {st === '' ? 'All' : (STATUS_LABEL[st]?.label || st)}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {loading ? (
                <div className="py-10 flex justify-center">
                  <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              ) : (data.items || []).length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No timesheets match this filter</p>
              ) : (data.items || []).map(renderRow)}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employees" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Per-Employee Summary</CardTitle>
              <CardDescription>{monthLabel} aggregate</CardDescription>
            </CardHeader>
            <CardContent>
              {(data.by_employee || []).length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No data for this month</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground border-b">
                        <th className="pb-2 font-medium">Employee</th>
                        <th className="pb-2 font-medium">Department</th>
                        <th className="pb-2 font-medium text-right">Days</th>
                        <th className="pb-2 font-medium text-right">Hours</th>
                        <th className="pb-2 font-medium text-right">Approved</th>
                        <th className="pb-2 font-medium text-right">Rejected</th>
                        <th className="pb-2 font-medium text-right">Pending</th>
                        <th className="pb-2 font-medium text-right">Late</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_employee.map((e, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2 font-medium">{e.employee_name}</td>
                          <td className="py-2 text-muted-foreground">{e.department || '—'}</td>
                          <td className="py-2 text-right">{e.days_logged}</td>
                          <td className="py-2 text-right">{(e.total_hours || 0).toFixed(1)}h</td>
                          <td className="py-2 text-right text-green-700">{e.approved}</td>
                          <td className="py-2 text-right text-red-600">{e.rejected}</td>
                          <td className="py-2 text-right text-amber-600">{e.pending}</td>
                          <td className="py-2 text-right">
                            {e.late_count > 0 && <Badge variant="destructive" className="text-[10px]">{e.late_count}</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!actionTs} onOpenChange={(o) => !o && setActionTs(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? 'HR Final Approval' : 'HR Reject'} —{' '}
              {actionTs?.employee_name} ({actionTs?.date})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {actionType === 'approve'
                ? 'Locks this timesheet permanently. WFH attendance will be marked verified for this date.'
                : 'Rejection sends the timesheet back to the employee. They must correct & resubmit.'}
            </p>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={actionType === 'reject' ? 'Reason for rejection (required)' : 'Optional note'}
              className="min-h-[80px]"
              data-testid="hr-action-comment"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionTs(null)}>Cancel</Button>
            <Button
              onClick={submitAction}
              disabled={submitting}
              variant={actionType === 'reject' ? 'destructive' : 'default'}
              data-testid="hr-action-submit"
            >
              {submitting ? 'Saving…' : (actionType === 'approve' ? 'Approve & Lock' : 'Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
